/**
 * Application entry point.
 *
 * Provider order (outside-in) is intentional:
 *   GlobalErrorBoundary  — catches anything that escapes the tree
 *     QueryClientProvider — server-state cache available to every child
 *       App                — brand provider + route tree (in later phases)
 */

import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClientProvider } from '@tanstack/react-query'

import '@/lib/i18n'
import { GlobalErrorBoundary } from '@/components/ErrorBoundary'
import { queryClient } from '@/lib/queryClient'
import { registerServiceWorker } from '@/lib/sw/register'
import { probeRequiredFeatures } from '@/lib/compat/featureProbe'
import { renderUnsupportedBrowserPage } from '@/lib/compat/unsupportedBrowserPage'
import App from './App'

const rootEl = document.getElementById('root')
if (!rootEl) {
  throw new Error('Root element #root not found in index.html')
}

// The landing page is prerendered into #root at build time (for crawlers
// and AI answer engines — see src/prerender.tsx). On the homepage React
// re-renders the identical tree, so there's no visible change; on every
// other route, discard that static markup before mounting so an app route
// never flashes the marketing page before the SPA boots.
if (window.location.pathname !== '/') {
  rootEl.replaceChildren()
}

// Per BLIND_SPOTS §1.9: refuse to boot React on a browser that can't
// run the cleaner critical path. Surfaces a vanilla-DOM fallback page
// instead of the white-screen-of-death the user would otherwise see.
const probe = probeRequiredFeatures()
if (!probe.ok) {
  renderUnsupportedBrowserPage(rootEl, probe.missing)
} else {
  createRoot(rootEl).render(
    <StrictMode>
      <GlobalErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <App />
        </QueryClientProvider>
      </GlobalErrorBoundary>
    </StrictMode>,
  )

  // Service worker — production only.
  //
  // Auto-apply updates: when a new SW is installed and waiting, post
  // SKIP_WAITING immediately. The 'controllerchange' listener inside
  // registerServiceWorker() then reloads the page exactly once, so the
  // new bundle takes over without the user touching anything.
  //
  // Why auto-apply instead of a "Refresh available" banner: this is a
  // field-ops PWA. Cleaners and supervisors don't notice toasts mid-
  // shift, and the cost of running an old build is real (the 2026-05-18
  // ABC SMT incident — every UI fix shipped to git stayed invisible on
  // Maria's device for the entire day because no code told the waiting
  // SW to take over). The cost of a one-tap reload is negligible.
  // Mutations in flight are protected by the existing offline mutation
  // queue (src/lib/sync/queue.ts), so a reload mid-submit doesn't lose
  // work.
  if (import.meta.env.PROD) {
    registerServiceWorker({
      onUpdateAvailable: (applyUpdate) => { applyUpdate() },
    }).catch((e: unknown) => {
      const message = e instanceof Error ? e.message : 'Service worker registration failed'
      document.dispatchEvent(new CustomEvent('sw-register-error', { detail: message }))
    })
  }
}
